import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import styles from './PersonalLayout.module.css';
import routes from 'routes';

import logoImage from 'assets/images/logo.png';
import Footer from 'components/common/Footer';
import Loading from 'components/common/Loading';
import SimpleProfile from 'components/common/SimpleProfile';
import ChangePasswordModal from 'components/personal/ChangePasswordModal';
import AdminLayout from 'layouts/AdminLayout';

import useEnsureClubs, { ClubStatus } from 'hooks/useEnsureClubs';
import { getToken, getUserInfo } from 'services/auth/tokenService';
import { logout } from 'api/authApi';
import { logout as logoutSlice } from 'services/user/userSlice';

// The personal-portal shell. The personal dashboard is reached the same way by
// everyone, but the chrome around it depends on whether the user belongs to a club:
//   • club member  → the full club shell (AdminLayout: menu + club switching), so
//                     they can hop back into any of their clubs from here.
//   • club-less     → the bare shell below (just logo + profile menu), since there
//                     is no club to switch into.
const PersonalLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const status = useEnsureClubs();

  const [showMenu, setShowMenu] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const menuRef = useRef(null);

  let userInfo = {};
  try {
    userInfo = JSON.parse(getUserInfo()) || {};
  } catch (e) {
    userInfo = {};
  }

  useEffect(() => {
    if (!getToken()) {
      navigate(routes.auth.login);
    }
  }, [navigate]);

  useEffect(() => {
    if (showMenu) {
      const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setShowMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleLogout = () => {
    logout().then(() => {
      dispatch(logoutSlice());
      navigate(routes.client.home);
    });
  };

  if (status === ClubStatus.Loading) {
    return <Loading />;
  }

  // Club members keep the full club shell (menu + switching) on their personal view.
  if (status === ClubStatus.HasClubs) {
    return <AdminLayout />;
  }

  // Club-less platform citizen → the bare personal shell.
  return (
    <>
      <header className={styles.topNav}>
        <div className={styles.navLeft}>
          <Link to={routes.personal.profile} className={styles.logoLink}>
            <img src={logoImage} alt="SplitFast" className={styles.logoImage} />
          </Link>
        </div>
        <div ref={menuRef} className={styles.profileDropdown}>
          <SimpleProfile
            user={userInfo}
            onClick={() => setShowMenu((s) => !s)}
            isActive={showMenu}
          />
          {showMenu && (
            <div className={styles.profileMenu}>
              <button
                className={styles.profileMenuItem}
                onClick={() => {
                  setShowMenu(false);
                  navigate(routes.personal.profile);
                }}
              >
                My Profile
              </button>
              <button
                className={styles.profileMenuItem}
                onClick={() => {
                  setShowMenu(false);
                  setPwOpen(true);
                }}
              >
                Reset my password
              </button>
              <button className={styles.profileMenuItem} onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </header>
      <main className={styles.content}>
        <Outlet />
      </main>
      <ChangePasswordModal isOpen={pwOpen} onClose={() => setPwOpen(false)} />
      <Footer />
    </>
  );
};

export default PersonalLayout;
